import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  CheckSquare, Square, Share2, Tag, Combine, Trash2, Check, Map,
} from "lucide-react";
import { ActionBtn } from "@/components/gallery/ActionBtn";

/**
 * Bottom floating action bar shown while Gallery is in select mode.
 *
 * On mobile the count + select-all live on the top row and the actions
 * flex-wrap on the second row so 5 destructive buttons don't blow out
 * the pill; on desktop everything sits on one line.
 *
 * All state + handlers live in Gallery.jsx — this component is a pure
 * presentational shell so the split is behavior-preserving.
 */
export const GallerySelectActionBar = ({
  selected,
  paged,
  items,
  merging,
  onSelectAllOnPage,
  onOpenBulkShare,
  onOpenBulkTag,
  onOpenBulkMarkReviewed,
  onOpenBulkCorridor,
  onMergeIntent,
  onOpenBulkDelete,
  onExit,
}) => {
  const { t } = useTranslation();
  const visibleIds = (paged?.items || []).map((i) => i.scan_id);
  const allOnPageSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));

  const openMarkReviewed = () => {
    // Only the currently-unreviewed scans qualify — nudge with a toast when
    // the user's selection has none.
    const ids = Array.from(selected).filter((sid) => {
      const it = items?.find((x) => x.scan_id === sid);
      return it && it.needs_revisit === true;
    });
    if (ids.length === 0) {
      toast.message(t("gallery.markNoUnreviewedInSelection"));
      return;
    }
    onOpenBulkMarkReviewed(ids);
  };

  return (
    <div
      className="fixed bottom-24 md:bottom-6 left-1/2 -translate-x-1/2 w-[calc(100%-2rem)] max-w-[448px] md:max-w-[720px] bg-leaf-ink text-white rounded-3xl shadow-botanicalLg px-3 py-2.5 z-30"
      data-testid="select-action-bar"
    >
      <div className="flex flex-col md:flex-row md:items-center gap-2">
        {/* Row 1 on mobile / left segment on desktop */}
        <div className="flex items-center justify-between md:justify-start gap-2 md:gap-3">
          <button
            onClick={onSelectAllOnPage}
            data-testid="select-all-button"
            aria-pressed={allOnPageSelected}
            className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/15 transition whitespace-nowrap"
          >
            {allOnPageSelected ? (
              <>
                <CheckSquare className="w-3.5 h-3.5" strokeWidth={1.5} />
                {t("gallery.deselectAllOnPage")}
              </>
            ) : (
              <>
                <Square className="w-3.5 h-3.5" strokeWidth={1.5} />
                {t("gallery.selectAllOnPage")}
              </>
            )}
          </button>
          <span
            className="text-xs text-cream/90 whitespace-nowrap"
            data-testid="select-count"
          >
            {t("gallery.selectedCount", { n: selected.size })}
          </span>
        </div>

        {/* Row 2 on mobile / right segment on desktop */}
        <div className="flex items-center gap-1.5 md:gap-2 md:ml-auto justify-end flex-wrap">
          <ActionBtn
            testId="bulk-share-trigger"
            onClick={onOpenBulkShare}
            disabled={selected.size === 0}
            icon={<Share2 className="w-3.5 h-3.5" strokeWidth={1.5} />}
            label={t("gallery.shareSelected")}
          />
          <ActionBtn
            testId="bulk-tag-trigger"
            onClick={onOpenBulkTag}
            disabled={selected.size === 0}
            icon={<Tag className="w-3.5 h-3.5" strokeWidth={1.5} />}
            label={t("gallery.tagSelected")}
          />
          <ActionBtn
            testId="bulk-mark-reviewed-trigger"
            onClick={openMarkReviewed}
            disabled={selected.size === 0}
            icon={<CheckSquare className="w-3.5 h-3.5" strokeWidth={1.5} />}
            label={t("gallery.markReviewedSelected")}
          />
          <ActionBtn
            testId="bulk-corridor-trigger"
            onClick={onOpenBulkCorridor}
            disabled={selected.size === 0}
            icon={<Map className="w-3.5 h-3.5" strokeWidth={1.5} />}
            label={t("gallery.corridorSelected")}
          />
          <ActionBtn
            testId="bulk-merge-trigger"
            onClick={onMergeIntent}
            disabled={selected.size < 2 || merging}
            icon={<Combine className="w-3.5 h-3.5" strokeWidth={1.5} />}
            label={t("gallery.mergeSelected")}
          />
          <ActionBtn
            testId="bulk-delete-trigger"
            onClick={onOpenBulkDelete}
            disabled={selected.size === 0}
            icon={<Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />}
            label={t("gallery.deleteSelected")}
            tone="danger"
          />
          <ActionBtn
            testId="bulk-done-trigger"
            onClick={onExit}
            icon={<Check className="w-3.5 h-3.5" strokeWidth={1.5} />}
            label={t("gallery.selectDone")}
            tone="primary"
          />
        </div>
      </div>
    </div>
  );
};

export default GallerySelectActionBar;
